import { ApprovalStep } from './expense.model';
import { PHIDataType } from './audit-log.model';

export enum DecisionStatus {
  draft = 'draft',
  pendingApproval = 'pending_approval',
  approved = 'approved',
  rejected = 'rejected',
  implemented = 'implemented',
  reversed = 'reversed',
}

export const DecisionStatusLabels: Record<DecisionStatus, string> = {
  [DecisionStatus.draft]: 'Draft',
  [DecisionStatus.pendingApproval]: 'Pending Approval',
  [DecisionStatus.approved]: 'Approved',
  [DecisionStatus.rejected]: 'Rejected',
  [DecisionStatus.implemented]: 'Implemented',
  [DecisionStatus.reversed]: 'Reversed',
};

export enum DecisionImpact {
  low = 'low',
  medium = 'medium',
  high = 'high',
  critical = 'critical',
}

export const DecisionImpactLabels: Record<DecisionImpact, string> = {
  [DecisionImpact.low]: 'Low',
  [DecisionImpact.medium]: 'Medium',
  [DecisionImpact.high]: 'High',
  [DecisionImpact.critical]: 'Critical',
};

export interface Decision {
  id: string;

  // Source
  meetingId: string;
  meetingTitle: string;
  sourceTimestampStart?: number; // seconds into transcript
  sourceTimestampEnd?: number;
  sourceTextSnippet?: string; // Original text from transcript

  // Decision Content
  title: string;
  description: string;
  rationale?: string; // Why this decision was made
  alternatives?: string[]; // Other options considered

  // Classification
  category: string; // 'product', 'engineering', 'business', etc.
  impact: DecisionImpact;

  // Stakeholders
  decisionMaker: string; // Name of person who made decision
  decisionMakerId?: string; // User ID if available
  affectedParties: string[]; // Who is impacted

  // Implementation
  implementationDate?: Date;
  implementationNotes?: string;

  // Status & Approval
  status: DecisionStatus;
  approvalWorkflow: ApprovalStep[];

  // PHI Detection
  containsPHI: boolean;
  phiDataTypes: PHIDataType[];
  phiNotes?: string;

  // AI Confidence
  aiConfidence: number; // 0-1
  aiExtractionNotes?: string;

  // Metadata
  tags: string[];
  relatedDecisionIds: string[]; // Links to related decisions
  customFields?: Record<string, unknown>;

  // Audit
  createdBy: string; // AI system initially
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  rejectedBy?: string;
  rejectedByName?: string;
  rejectedAt?: Date;
  rejectionReason?: string;
}

export interface DecisionAnalytics {
  totalDecisions: number;
  byStatus: Record<DecisionStatus, number>;
  byImpact: Record<DecisionImpact, number>;
  avgImplementationTime: number; // days
  pendingApprovalCount: number;
  implementedCount: number;
  reversedCount: number;
}
